import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { flushSync } from 'react-dom';
import api from '../../../api/axios';
import { toAssetUrl } from '../../../utils/assetBase';

// Banket menyusu — A4 çap / PDF (ağ fon). Hamısı bir səhifəyə sığır (ölçü avtomatik seçilir).
// İki variant: «Ağ-qara» (hər şey qara) və «Rəngli» (bəzək/başlıq/qiymət → QR menyu «Buttonlar / vurğu» rəngi).
// Brauzerin «Çap» pəncərəsində «PDF kimi saxla» seçilsə PDF alınır. (C# çapı sonra.)

const FONT_LINK_ID = 'banquet-print-fonts';
const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Great+Vibes&family=Cormorant+Garamond:ital,wght@0,500;0,600;0,700;1,500;1,600&display=swap';
const hasWebView = () => typeof window !== 'undefined' && !!window.chrome?.webview?.postMessage;

// Çap HTML-inə şriftlərin ÖZÜNÜ yerləşdir (base64): C#-ın çap pəncərəsi Google Fonts-u yükləməyə
// macal tapmırdı → vərəq ehtiyat Times şrifti ilə (daha enli) çap olunur, başlıq iki sətrə düşür,
// aşağı hissə vərəqdən daşırdı. Yalnız latin + latin-ext (ə, ı, ğ, ş, ç, ö, ü) dəstləri. Bir dəfə yüklənib keşlənir.
let inlineFontCssPromise = null;
const toDataUrl = async (url) => {
    const blob = await (await fetch(url)).blob();
    return new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(blob);
    });
};
const getInlineFontCss = () => {
    if (!inlineFontCssPromise) {
        inlineFontCssPromise = (async () => {
            const css = await (await fetch(FONT_HREF)).text();
            const blocks = css.split('@font-face').slice(1).map((b) => '@font-face' + b.slice(0, b.indexOf('}') + 1));
            const keep = blocks.filter((b) => /U\+0000-00FF|U\+0100-02/i.test(b));
            const out = [];
            for (const b of keep) {
                const m = b.match(/url\((https:[^)]+)\)/);
                if (!m) continue;
                out.push(b.replace(m[1], await toDataUrl(m[1])));
            }
            return out.join('\n');
        })().catch(() => { inlineFontCssPromise = null; return ''; });
    }
    return inlineFontCssPromise;
};
const INK = '#111111';
const SOFT = '#555555';

const fmtPrice = (v) => {
    const n = Number(v || 0);
    return Number.isInteger(n) ? String(n) : n.toFixed(2);
};

// Azərbaycan nömrəsi: 994XXXXXXXXX → +994 XX XXX XX XX
const fmtPhone = (p) => {
    const d = String(p || '').replace(/\D/g, '');
    if (d.length === 12 && d.startsWith('994')) return `+994 ${d.slice(3, 5)} ${d.slice(5, 8)} ${d.slice(8, 10)} ${d.slice(10)}`;
    if (d.length === 10 && d.startsWith('0')) return `+994 ${d.slice(1, 3)} ${d.slice(3, 6)} ${d.slice(6, 8)} ${d.slice(8)}`;
    return String(p || '');
};

// Qısa, qeydsiz siyahı → bir sətirdə «Sirab · Limonad · Təbii şirə»
function isInline(c) {
    const its = c.items || [];
    if (its.length < 2 || its.length > 4) return false;
    if (its.some((i) => i.note)) return false;
    return its.map((i) => i.name).join(' · ').length <= 34;
}

// Kateqoriyaları SIRASINI pozmadan N sütuna böl ki, ən uzun sütun mümkün qədər qısa olsun
const catWeight = (c) => (isInline(c) ? 1 : c.items.length) + 2.4;
const splitColumns = (cats, n) => {
    const w = cats.map(catWeight);
    let best = null;
    const rec = (start, left, acc) => {
        if (left === 1) {
            const parts = [...acc, [start, cats.length]];
            const max = Math.max(...parts.map(([a, b]) => w.slice(a, b).reduce((x, y) => x + y, 0)));
            if (!best || max < best.max) best = { max, parts };
            return;
        }
        for (let end = start + 1; end <= cats.length - left + 1; end++) rec(end, left - 1, [...acc, [start, end]]);
    };
    const k = Math.min(n, cats.length);
    if (k <= 1) return [cats];
    rec(0, k, []);
    return best.parts.map(([a, b]) => cats.slice(a, b));
};

// ── İkonlar (sadə xətt) ──
const Icon = ({ name, color, size }) => {
    const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round', style: { flexShrink: 0 } };
    if (name === 'phone') return <svg {...p}><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2z" /></svg>;
    if (name === 'chat') return <svg {...p}><path d="M21 11.5a8.4 8.4 0 0 1-12.4 7.4L3 21l2.1-5.4A8.4 8.4 0 1 1 21 11.5z" /></svg>;
    if (name === 'insta') return <svg {...p}><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="0.6" fill={color} /></svg>;
    return <svg {...p}><path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" /><circle cx="12" cy="9.5" r="2.5" /></svg>;
};

// Başlıq bölücü: solğunlaşan xətt ◆◆◆ solğunlaşan xətt
const Divider = ({ color, width }) => (
    <svg width={width} height="14" viewBox="0 0 400 14" aria-hidden="true" style={{ display: 'block', margin: '0 auto' }}>
        <defs>
            <linearGradient id="bqfadeL" x1="0" x2="1"><stop offset="0" stopColor={color} stopOpacity="0" /><stop offset="1" stopColor={color} /></linearGradient>
            <linearGradient id="bqfadeR" x1="0" x2="1"><stop offset="0" stopColor={color} /><stop offset="1" stopColor={color} stopOpacity="0" /></linearGradient>
        </defs>
        <rect x="0" y="6.4" width="150" height="1.2" fill="url(#bqfadeL)" />
        <rect x="250" y="6.4" width="150" height="1.2" fill="url(#bqfadeR)" />
        {[175, 200, 225].map((x) => <path key={x} d={`M${x} 2 L${x + 5} 7 L${x} 12 L${x - 5} 7 Z`} fill={color} />)}
    </svg>
);

// Kateqoriya başlığının altındakı kiçik xətt + ◆
const CatMark = ({ color, w }) => (
    <svg width={w} height="8" viewBox="0 0 60 8" aria-hidden="true" style={{ display: 'block', margin: '0 auto' }}>
        <rect x="0" y="3.4" width="60" height="1.2" fill={color} />
        <path d="M30 0 L34 4 L30 8 L26 4 Z" fill={color} />
    </svg>
);

const Corner = ({ color, pos }) => {
    const s = { position: 'absolute', width: '9mm', height: '9mm', borderColor: color, borderStyle: 'solid', borderWidth: 0 };
    const at = {
        tl: { top: '4mm', left: '4mm', borderTopWidth: 2.5, borderLeftWidth: 2.5 },
        tr: { top: '4mm', right: '4mm', borderTopWidth: 2.5, borderRightWidth: 2.5 },
        bl: { bottom: '4mm', left: '4mm', borderBottomWidth: 2.5, borderLeftWidth: 2.5 },
        br: { bottom: '4mm', right: '4mm', borderBottomWidth: 2.5, borderRightWidth: 2.5 },
    }[pos];
    return <div style={{ ...s, ...at }} />;
};

export default function BanquetPrint() {
    const { id } = useParams();
    const [sp] = useSearchParams();
    // embed=1 → kassirin «Banket menyu» pəncərəsi səhifəni gizli iframe-də açır: sığdırılmış vərəqin
    // HTML-i valideynə göndərilir (çap C#-a oradan gedir). Alətlər paneli göstərilmir.
    const embed = sp.get('embed') === '1';
    const [data, setData] = useState(null);
    const [err, setErr] = useState('');
    const [variant, setVariant] = useState(sp.get('variant') === 'color' ? 'color' : 'mono'); // mono | color
    const [fitTick, setFitTick] = useState(0);
    const fontsReadyRef = useRef(false); // gizli rejimdə HTML yalnız şriftlər hazır olandan sonra göndərilir
    const [scale, setScale] = useState(1);
    const sheetRef = useRef(null);
    const navigate = useNavigate();
    const desktop = hasWebView();

    // ── Desktop (C#) çapı: nüsxə sayı + printer (default A4) + masaüstünə PDF ──
    const [printers, setPrinters] = useState([]);
    const [dlg, setDlg] = useState(null);          // { copies, printer }
    const [busy, setBusy] = useState('');          // '' | 'print' | 'pdf'
    useEffect(() => {
        if (!desktop) return;
        api.get('/printers').then((r) => setPrinters((r.data?.data || []).filter((p) => Number(p.active ?? 1) === 1))).catch(() => {});
    }, [desktop]);
    const a4List = printers.filter((p) => p.printer_type === 'a4');
    const openDlg = () => setDlg({ copies: '1', printer: a4List[0]?.name || '' });

    // Vərəqi müstəqil HTML sənədinə çevir (C# gizli WebView2-də çap / PDF edir)
    const buildHtml = async () => {
        const sheet = sheetRef.current;
        if (!sheet) return '';
        const fontCss = await getInlineFontCss();   // alınmasa (offline) → adi link
        const fontTag = fontCss ? `<style>${fontCss}</style>` : `<link rel="stylesheet" href="${FONT_HREF}">`;
        return `<!doctype html><html><head><meta charset="utf-8"><base href="${window.location.origin}/">`
            + fontTag
            + `<style>@page{size:A4 portrait;margin:0}html,body{margin:0;padding:0;width:210mm;height:297mm;overflow:hidden;background:#fff}`
            + `.bq-sheet{box-shadow:none!important;margin:0!important}*{-webkit-print-color-adjust:exact;print-color-adjust:exact}</style>`
            + `</head><body>${sheet.outerHTML}</body></html>`;
    };

    // C#-dən gələn tək nəticəni gözlə (type + requestId), vaxt bitsə null
    const waitResult = (type, reqId, ms) => new Promise((resolve) => {
        const wv = window.chrome.webview;
        let done = false;
        const handler = (ev) => {
            const d = ev?.data;
            if (!d || d.type !== type) return;
            if (reqId && d.requestId && d.requestId !== reqId) return;
            done = true; wv.removeEventListener('message', handler); resolve(d);
        };
        wv.addEventListener('message', handler);
        setTimeout(() => { if (!done) { wv.removeEventListener('message', handler); resolve(null); } }, ms);
    });

    const doDesktopPrint = async () => {
        if (!dlg) return;
        const copies = Math.min(99, Math.max(1, parseInt(dlg.copies, 10) || 1));
        const printer = dlg.printer;
        if (!printer) { toast.error('Printer seçin.'); return; }
        const html = await buildHtml();
        if (!html) return;
        const requestId = `bq_${Date.now()}`;
        setBusy('print');
        const wait = waitResult('print-a4-result', requestId, 60000);
        window.chrome.webview.postMessage({ type: 'print-a4', printer, copies, html, requestId, a4exact: true });
        setDlg(null);
        toast(`🖨️ ${copies} vərəq → ${printer}`, { duration: 2500 });
        const r = await wait;
        setBusy('');
        if (r && r.ok) toast.success(`✓ ${copies} vərəq çapa göndərildi (${printer}).`);
        else if (r) toast.error(r.error || `Çap alınmadı (${r.status || 'xəta'}).`);
        // r === null → köhnə C# versiyası nəticə göndərmir; çap yenə də gedir
    };

    const doDesktopPdf = async () => {
        const html = await buildHtml();
        if (!html) return;
        const safe = `${data?.tenant?.name || 'Banket'}_${fmtPrice(data?.banquet?.price_per_person)}AZN`.replace(/[^\p{L}\p{N}]+/gu, '_').slice(0, 50);
        const filename = `Banket_menyu_${safe}.pdf`;
        setBusy('pdf');
        setDlg(null);
        const wait = waitResult('export-pdf-result', '', 60000);
        window.chrome.webview.postMessage({ type: 'export-pdf', filename, html, a4exact: true });
        const r = await wait;
        setBusy('');
        if (r && r.ok) toast.success(`📄 PDF masaüstünə saxlanıldı: ${r.path || filename}`, { duration: 5000 });
        else toast.error(r?.error || 'PDF yaradılmadı.');
    };

    useEffect(() => {
        if (!document.getElementById(FONT_LINK_ID)) {
            const l = document.createElement('link');
            l.id = FONT_LINK_ID;
            l.rel = 'stylesheet';
            l.href = FONT_HREF;
            document.head.appendChild(l);
        }
    }, []);

    useEffect(() => {
        api.get(`/banquet/${id}/print`)
            .then((r) => setData(r.data))
            .catch((e) => setErr(e.response?.data?.msg || 'Yüklənmədi.'));
    }, [id]);

    useEffect(() => {
        if (data?.banquet) document.title = `${data.tenant?.name || ''} · Banket menyusu`;
    }, [data]);

    // Ən böyük sığan ölçü — ikili axtarış (flushSync: render + ölçmə dərhal)
    useLayoutEffect(() => {
        if (!data) return;
        let cancelled = false;
        const fit = async () => {
            // Əsl şriftlər (Cormorant, Great Vibes) yüklənməmiş ölçmə → ehtiyat şriftlə hesablanır, çapda isə
            // əsl şrift gəlir və mətn vərəqdən daşır. Ona görə əvvəl şriftləri açıq yüklə (maks 4 san).
            try {
                // Google Fonts CSS-i hələ gəlməyibsə @font-face qaydaları yoxdur → əvvəl onu gözlə
                const link = document.getElementById(FONT_LINK_ID);
                if (link && !link.sheet) {
                    await new Promise((r) => {
                        link.addEventListener('load', r, { once: true });
                        link.addEventListener('error', r, { once: true });
                        setTimeout(r, 4000);
                    });
                }
                const loads = Promise.all([
                    document.fonts.load("600 24px 'Cormorant Garamond'"),
                    document.fonts.load("500 24px 'Cormorant Garamond'"),
                    document.fonts.load("italic 500 24px 'Cormorant Garamond'"),
                    document.fonts.load("700 24px 'Cormorant Garamond'"),
                    document.fonts.load("48px 'Great Vibes'"),
                ]);
                await Promise.race([loads, new Promise((r) => setTimeout(r, 4000))]);
                await document.fonts.ready;
            } catch { /* ignore */ }
            fontsReadyRef.current = true;
            const fits = (v) => {
                flushSync(() => setScale(v));
                const sheet = sheetRef.current;
                return !!sheet && sheet.scrollHeight <= sheet.clientHeight;
            };
            let lo = 0.45, hi = 1.7;
            if (fits(hi)) { if (!cancelled) setFitTick((t) => t + 1); return; }
            for (let i = 0; i < 14 && !cancelled; i++) {
                const mid = (lo + hi) / 2;
                if (fits(mid)) lo = mid; else hi = mid;
            }
            if (!cancelled) { flushSync(() => setScale(lo * 0.96)); setFitTick((t) => t + 1); }
        };
        fit();
        const onFonts = () => { if (!cancelled) fit(); };
        try { document.fonts?.addEventListener('loadingdone', onFonts); } catch { /* ignore */ }
        return () => {
            cancelled = true;
            try { document.fonts?.removeEventListener('loadingdone', onFonts); } catch { /* ignore */ }
        };
    }, [data, variant]);

    // Gizli (embed) rejim: vərəq sığdırılan kimi HTML-i valideyn pəncərəyə göndər
    useEffect(() => {
        if (!embed || !fitTick || !fontsReadyRef.current) return;
        let alive = true;
        (async () => {
            const html = await buildHtml();
            if (!alive) return;
            try { window.parent?.postMessage({ type: 'bq-sheet-html', id: String(id), html }, window.location.origin); } catch { /* ignore */ }
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [embed, fitTick]);
    useEffect(() => {
        if (!embed || !err) return;
        try { window.parent?.postMessage({ type: 'bq-sheet-html', id: String(id), error: err }, window.location.origin); } catch { /* ignore */ }
    }, [embed, err, id]);

    if (err) return <div style={{ padding: 40, fontFamily: 'sans-serif', color: '#b91c1c' }}>{err}</div>;
    if (!data) return <div style={{ padding: 40, fontFamily: 'sans-serif', color: '#6b7280' }}>Yüklənir...</div>;

    const { banquet: b, tenant: t } = data;
    const A = variant === 'color' ? (t.accent || '#0090bb') : INK; // vurğu rəngi
    const cats = b.categories || [];
    const weight = cats.reduce((n, c) => n + catWeight(c), 0);
    const columns = cats.length <= 1 ? 1 : (weight > 52 && cats.length >= 3 ? 3 : 2);
    const colGroups = splitColumns(cats, columns);
    const base = 15 * scale; // px
    // Əlaqə sətri bir sətrə sığsın: şrift ölçüsü vərəqin miqyasından asılı deyil (uzun ünvanda bir az kiçilir)
    const contactChars = [t.phone, t.whatsapp, t.instagram, t.address].filter(Boolean).join('').length + 20;
    const foot = Math.min(base * 1.02, 15, Math.max(10.5, 1150 / contactChars));

    const serif = "'Cormorant Garamond', 'Times New Roman', serif";
    const script = "'Great Vibes', 'Times New Roman', cursive";
    const genericName = /^banket(\s+menyu(su)?)?$/i.test(String(b.name || '').trim());
    const subtitle = !b.name || genericName ? 'Hər qonaq üçün tam süfrə' : b.name;

    const contacts = [
        t.phone && { icon: 'phone', text: fmtPhone(t.phone) },
        t.whatsapp && { icon: 'chat', text: fmtPhone(t.whatsapp) },
        t.instagram && { icon: 'insta', text: String(t.instagram).replace(/^https?:\/\/(www\.)?instagram\.com\//, '').replace(/\/$/, '').replace(/^@?/, '@') },
        t.address && { icon: 'pin', text: t.address },
    ].filter(Boolean);

    return (
        <div className={`bq-print-root${embed ? ' bq-embed' : ''}`} style={{ minHeight: '100vh', background: embed ? '#fff' : '#e5e7eb', padding: embed ? 0 : '24px 12px' }}>
            <style>{`
                @page { size: A4 portrait; margin: 0; }
                @media print {
                    html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
                    .bq-sheet { height: 296mm !important; page-break-after: avoid; break-after: avoid; }
                    .bq-print-root { padding: 0 !important; background: none !important; }
                    .bq-toolbar { display: none !important; }
                    .bq-sheet { box-shadow: none !important; margin: 0 !important; }
                }
                .bq-sheet, .bq-sheet * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                .bq-cat { break-inside: avoid; page-break-inside: avoid; }
                .bq-embed .bq-toolbar { display: none !important; }
            `}</style>

            {/* Alətlər (çapda görünmür) */}
            <div className="bq-toolbar" style={{ maxWidth: '210mm', margin: '0 auto 14px', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontFamily: 'system-ui, sans-serif' }}>
                <button onClick={() => navigate('/admin/banquet')}
                    style={{ background: '#fff', color: '#374151', border: 0, borderRadius: 10, padding: '10px 14px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                    ← Geri
                </button>
                {desktop ? (
                    <>
                        <button onClick={openDlg} disabled={!!busy}
                            style={{ background: '#111827', color: '#fff', border: 0, borderRadius: 10, padding: '10px 18px', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
                            {busy === 'print' ? 'Çap olunur...' : '🖨️ Çap et'}
                        </button>
                        <button onClick={doDesktopPdf} disabled={!!busy}
                            style={{ background: '#fff', color: '#111827', border: 0, borderRadius: 10, padding: '10px 16px', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
                            {busy === 'pdf' ? 'PDF hazırlanır...' : '📄 Masaüstünə PDF'}
                        </button>
                    </>
                ) : (
                    <button onClick={() => window.print()}
                        style={{ background: '#111827', color: '#fff', border: 0, borderRadius: 10, padding: '10px 18px', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                        🖨️ Çap et / PDF saxla
                    </button>
                )}
                <div style={{ display: 'flex', background: '#fff', borderRadius: 10, padding: 3 }}>
                    {[['mono', 'Ağ-qara'], ['color', 'Rəngli']].map(([k, label]) => (
                        <button key={k} onClick={() => setVariant(k)}
                            style={{ border: 0, borderRadius: 8, padding: '7px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                                background: variant === k ? '#111827' : 'transparent', color: variant === k ? '#fff' : '#4b5563' }}>
                            {k === 'color' && <span style={{ width: 12, height: 12, borderRadius: 99, background: t.accent || '#0090bb', display: 'inline-block' }} />}
                            {label}
                        </button>
                    ))}
                </div>
                {!desktop && <span style={{ fontSize: 12, color: '#6b7280' }}>Çap pəncərəsində: Kağız <b>A4</b>, Kənar boşluqlar <b>Yox</b>, Miqyas <b>100%</b>. PDF üçün printer yerinə «PDF kimi saxla».</span>}
            </div>

            {/* Desktop çap pəncərəsi */}
            {dlg && (
                <div className="bq-toolbar" onMouseDown={() => setDlg(null)}
                    style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: 'system-ui, sans-serif' }}>
                    <div onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => { if (e.key === 'Escape') setDlg(null); if (e.key === 'Enter') { e.preventDefault(); doDesktopPrint(); } }}
                        style={{ width: '100%', maxWidth: 380, background: '#fff', borderRadius: 18, padding: 20, boxShadow: '0 20px 50px rgba(0,0,0,.3)' }}>
                        <div style={{ fontSize: 17, fontWeight: 800, color: '#111827', marginBottom: 14 }}>🖨️ Banket menyusunu çap et</div>

                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#4b5563', marginBottom: 6 }}>Neçə vərəq?</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                            <button type="button" onClick={() => setDlg({ ...dlg, copies: String(Math.max(1, (parseInt(dlg.copies, 10) || 1) - 1)) })}
                                style={{ width: 42, height: 42, borderRadius: 12, border: '2px solid #e5e7eb', background: '#fff', fontSize: 20, fontWeight: 800, cursor: 'pointer' }}>−</button>
                            <input autoFocus type="number" min="1" max="99" value={dlg.copies}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => setDlg({ ...dlg, copies: e.target.value })}
                                style={{ width: 80, height: 42, textAlign: 'center', fontSize: 20, fontWeight: 800, borderRadius: 12, border: '2px solid #93c5fd', outline: 'none' }} />
                            <button type="button" onClick={() => setDlg({ ...dlg, copies: String(Math.min(99, (parseInt(dlg.copies, 10) || 0) + 1)) })}
                                style={{ width: 42, height: 42, borderRadius: 12, border: '2px solid #e5e7eb', background: '#fff', fontSize: 20, fontWeight: 800, cursor: 'pointer' }}>+</button>
                        </div>

                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#4b5563', marginBottom: 6 }}>Printer</label>
                        {a4List.length === 0 && (
                            <div style={{ background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a', borderRadius: 10, padding: '8px 10px', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>
                                📄 A4 printer tapılmadı. {printers.length ? 'Hansı printerdən çap edək?' : 'Admin → Printerlər bölməsində printer əlavə edin və ya PDF kimi saxlayın.'}
                            </div>
                        )}
                        {printers.length > 0 && (
                            <select value={dlg.printer} onChange={(e) => setDlg({ ...dlg, printer: e.target.value })}
                                style={{ width: '100%', height: 42, borderRadius: 12, border: '2px solid #e5e7eb', padding: '0 10px', fontSize: 14, fontWeight: 600, background: '#fff' }}>
                                {!dlg.printer && <option value="">Printer seçin...</option>}
                                {(a4List.length ? a4List : printers).map((p) => (
                                    <option key={p.id || p.name} value={p.name}>
                                        {p.printer_type === 'a4' ? '📄 ' : '🧾 '}{p.label || p.name}
                                    </option>
                                ))}
                                {a4List.length > 0 && printers.some((p) => p.printer_type !== 'a4') && (
                                    <optgroup label="Digər printerlər">
                                        {printers.filter((p) => p.printer_type !== 'a4').map((p) => (
                                            <option key={`o_${p.id || p.name}`} value={p.name}>🧾 {p.label || p.name}</option>
                                        ))}
                                    </optgroup>
                                )}
                            </select>
                        )}

                        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
                            <button type="button" onClick={() => setDlg(null)}
                                style={{ flex: 1, height: 44, borderRadius: 12, border: '1px solid #e5e7eb', background: '#fff', fontWeight: 700, color: '#6b7280', cursor: 'pointer' }}>Ləğv</button>
                            <button type="button" onClick={doDesktopPdf}
                                style={{ flex: 1, height: 44, borderRadius: 12, border: '1px solid #e5e7eb', background: '#fff', fontWeight: 700, color: '#111827', cursor: 'pointer' }}>📄 PDF</button>
                            <button type="button" onClick={doDesktopPrint} disabled={!dlg.printer}
                                style={{ flex: 1.4, height: 44, borderRadius: 12, border: 0, background: '#111827', color: '#fff', fontWeight: 800, cursor: 'pointer', opacity: dlg.printer ? 1 : 0.4 }}>
                                🖨️ Çap et ({Math.max(1, parseInt(dlg.copies, 10) || 1)})
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* A4 vərəq */}
            <div ref={sheetRef} className="bq-sheet"
                style={{
                    width: '210mm', height: '297mm', margin: '0 auto', background: '#fff', color: INK,
                    boxShadow: '0 10px 30px rgba(0,0,0,.18)', position: 'relative', overflow: 'hidden', boxSizing: 'border-box',
                    padding: '14mm 15mm 12mm',
                }}>
                {/* Çərçivə: iki xətt + künc mötərizələri */}
                <div style={{ position: 'absolute', inset: '6mm', border: `1px solid ${A}`, pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', inset: '7.6mm', border: `0.6px solid ${A}`, opacity: 0.7, pointerEvents: 'none' }} />
                {['tl', 'tr', 'bl', 'br'].map((p) => <Corner key={p} color={A} pos={p} />)}

                <div style={{ position: 'relative', minHeight: '100%', display: 'flex', flexDirection: 'column', fontFamily: serif, fontSize: base, color: INK }}>
                    {/* Başlıq */}
                    <div style={{ textAlign: 'center' }}>
                        {t.logo_url && (
                            <img src={toAssetUrl(t.logo_url)} alt=""
                                style={{ height: 78 * scale, maxWidth: '40%', objectFit: 'contain', display: 'block', margin: '0 auto 2px',
                                    filter: variant === 'mono' ? 'grayscale(1) contrast(1.1)' : 'none' }} />
                        )}
                        <div style={{ fontFamily: script, fontSize: base * 3.6, lineHeight: 1.05, color: A }}>{t.name}</div>
                        <div style={{ margin: `${base * 0.5}px 0 ${base * 0.7}px` }}><Divider color={A} width={base * 26} /></div>
                        <div style={{ fontSize: base * 2.6, fontWeight: 600, letterSpacing: '0.32em', lineHeight: 1.1, paddingLeft: '0.32em' }}>BANKET MENYUSU</div>
                        <div style={{ fontStyle: 'italic', color: SOFT, fontSize: base * 1.1, marginTop: base * 0.3 }}>{subtitle}</div>
                    </div>

                    {/* Kateqoriyalar */}
                    <div style={{ flex: 1, display: 'flex', alignItems: 'stretch', marginTop: base * 1.8 }}>
                        {colGroups.map((group, gi) => (
                            <div key={gi} style={{ flex: 1, minWidth: 0, display: 'flex' }}>
                                {gi > 0 && (
                                    <div style={{ width: 1, alignSelf: 'stretch', margin: `0 ${base * 0.6}px`,
                                        background: `linear-gradient(to bottom, transparent, ${A}55 15%, ${A}55 85%, transparent)` }} />
                                )}
                                <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
                                    {group.map((c) => (
                                        <div key={c.name} className="bq-cat" style={{ marginBottom: base * 1.5 }}>
                                            <div style={{ fontSize: base * 0.92, fontWeight: 700, letterSpacing: '0.28em', textTransform: 'uppercase', color: A, paddingLeft: '0.28em' }}>
                                                {c.name}
                                            </div>
                                            <div style={{ margin: `${base * 0.25}px 0 ${base * 0.55}px` }}><CatMark color={A} w={base * 3.6} /></div>
                                            {isInline(c) ? (
                                                <div style={{ fontSize: base * 1.25, lineHeight: 1.5 }}>{c.items.map((i) => i.name).join(' · ')}</div>
                                            ) : c.items.map((it, i) => (
                                                <div key={i} style={{ fontSize: base * 1.25, lineHeight: 1.55 }}>
                                                    {it.name}
                                                    {it.note && <span style={{ fontStyle: 'italic', fontSize: base * 0.85, color: SOFT }}> ({it.note})</span>}
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Qiymət */}
                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: base * 0.6 }}>
                        <div style={{ borderTop: `1.5px solid ${A}`, borderBottom: `1.5px solid ${A}`, padding: `${base * 0.5}px ${base * 2.6}px`,
                            display: 'flex', alignItems: 'center', gap: base * 1.2 }}>
                            <span style={{ fontSize: base * 3.6, fontWeight: 600, lineHeight: 1, color: A, whiteSpace: 'nowrap' }}>{fmtPrice(b.price_per_person)} ₼</span>
                            <span style={{ textAlign: 'left', lineHeight: 1.3 }}>
                                <span style={{ display: 'block', fontWeight: 700, fontSize: base * 1.1 }}>bir nəfər üçün</span>
                                {b.min_guests > 0 && <span style={{ display: 'block', fontStyle: 'italic', fontSize: base * 1.05, color: SOFT }}>minimum {b.min_guests} nəfər</span>}
                            </span>
                        </div>
                    </div>

                    {/* Əlaqə */}
                    {contacts.length > 0 && (
                        <div style={{
                            marginTop: base * 2, paddingTop: base * 0.7, borderTop: `1px solid ${A}`,
                            display: 'flex', flexWrap: 'wrap', justifyContent: contacts.length > 2 ? 'space-between' : 'center',
                            columnGap: foot * 1.2, rowGap: foot * 0.3, fontSize: foot, fontWeight: 600,
                        }}>
                            {contacts.map((c, i) => (
                                <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: foot * 0.45, whiteSpace: 'nowrap' }}>
                                    <Icon name={c.icon} color={A} size={foot * 1.05} />{c.text}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
